import { useState, useEffect } from "react";
import axiosInstance from "../utils/axiosConfig";
import {
  Layout,
  Card,
  Input,
  Button,
  Space,
  Typography,
  Select,
  Form,
  Table,
  Tabs,
  Tag,
  App,
  Spin,
  Alert,
  Collapse,
  Divider,
  Empty,
  Row,
  Col,
} from "antd";
import {
  SearchOutlined,
  PlayCircleOutlined,
  CodeOutlined,
  FileTextOutlined,
  DownOutlined,
  RightOutlined,
  BookOutlined,
  GlobalOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  DollarOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { useEnv } from "../console/useEnv";
import { useInsideConsole } from "../console/insideConsole";
import {
  parsePostmanCollection,
  findEndpointById,
} from "../utils/apiCollection";

const { Sider, Content: AntContent } = Layout;
const { Title, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Token Bucket Rate Limiter Configuration
const BUCKET_CAPACITY = 50;
const REFILL_RATE = 1; // Tokens per minute
const REFILL_INTERVAL_MS = 60 * 1000;

export default function ApiDocumentation() {
  const { message } = App.useApp();
  // Inside the Console the endpoint navigator lives in the shell's left column
  // (components/console/ApiDocsNav) and selection is deep-linked via ?e=<id>, so this
  // page hides its own Collections sidebar and reads the selection from the URL.
  // Standalone (/documentation) keeps its self-contained sidebar.
  const insideConsole = useInsideConsole();
  const [searchParams] = useSearchParams();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [viewMode, setViewMode] = useState("input"); // "input" or "command"
  const [activeTab, setActiveTab] = useState("documentation"); // outer tab: documentation | request
  // Environment follows the Console's Preprod/Prod toggle so there's ONE env concept.
  // Outside the Console this falls back to the stored default (preprod), i.e. the
  // legacy standalone route behaves as before.
  const { env: consoleEnv } = useEnv();
  const [environment, setEnvironment] = useState(consoleEnv); // "prod" or "preprod"
  // Keep following the Console toggle whenever it changes.
  useEffect(() => {
    setEnvironment(consoleEnv);
  }, [consoleEnv]);
  const [requestData, setRequestData] = useState({});
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [showJsonView, setShowJsonView] = useState(false);
  const [commandText, setCommandText] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [nextRefillInSeconds, setNextRefillInSeconds] = useState(0);

  // Initialize token bucket
  const initializeTokenBucket = () => {
    const savedCredits = localStorage.getItem("api_credits");
    const lastRefill = localStorage.getItem("api_credits_last_refill");
    const now = Date.now();

    if (!savedCredits || !lastRefill) {
      // First time or after logout - initialize with full bucket
      localStorage.setItem("api_credits", BUCKET_CAPACITY.toString());
      localStorage.setItem("api_credits_last_refill", now.toString());
      return BUCKET_CAPACITY;
    }

    // Calculate tokens that should have been refilled
    const lastRefillTime = parseInt(lastRefill, 10);
    const timeElapsed = now - lastRefillTime;
    const minutesElapsed = timeElapsed / REFILL_INTERVAL_MS;
    const tokensToAdd = Math.floor(minutesElapsed * REFILL_RATE);

    const currentCredits = parseInt(savedCredits, 10);
    const newCredits = Math.min(BUCKET_CAPACITY, currentCredits + tokensToAdd);

    // Update last refill time if tokens were added
    if (tokensToAdd > 0) {
      localStorage.setItem("api_credits", newCredits.toString());
      localStorage.setItem("api_credits_last_refill", now.toString());
    }

    return newCredits;
  };

  const [remainingCredits, setRemainingCredits] = useState(() => {
    return initializeTokenBucket();
  });

  // Environment URLs - Use separate proxy paths for prod and preprod
  const environments = {
    prod: "/api/prod", // Proxied to the prod gateway
    preprod: "/api/preprod", // Proxied to the preprod gateway
  };

  // Brand API domain, derived from the served doc's own URLs (the backend serves a
  // brand-specific doc — e.g. vaultro.ai hosts for a Vaultro account). Default until
  // the doc loads; the doc is the source of truth so curl matches what the user's
  // brand actually calls.
  const [apiDomain, setApiDomain] = useState("verifyu.ai");

  // Full URLs for cURL command display (for user reference)
  const environmentDisplayUrls = {
    prod: `https://api.${apiDomain}`,
    preprod: `https://preprod-api.${apiDomain}`,
  };

  // Refill tokens periodically (every minute)
  useEffect(() => {
    const refillInterval = setInterval(() => {
      const currentCredits = parseInt(
        localStorage.getItem("api_credits") || "0",
        10
      );
      if (currentCredits < BUCKET_CAPACITY) {
        const newCredits = Math.min(
          BUCKET_CAPACITY,
          currentCredits + REFILL_RATE
        );
        localStorage.setItem("api_credits", newCredits.toString());
        localStorage.setItem("api_credits_last_refill", Date.now().toString());
        setRemainingCredits(newCredits);
        updateNextRefillTime();
      }
    }, REFILL_INTERVAL_MS);

    // Update next refill time display every second
    const timeUpdateInterval = setInterval(() => {
      const lastRefill = parseInt(
        localStorage.getItem("api_credits_last_refill") || "0",
        10
      );
      const now = Date.now();
      const timeSinceLastRefill = now - lastRefill;
      const timeToNextRefill =
        REFILL_INTERVAL_MS - (timeSinceLastRefill % REFILL_INTERVAL_MS);
      setNextRefillInSeconds(Math.ceil(timeToNextRefill / 1000));
    }, 1000);

    return () => {
      clearInterval(refillInterval);
      clearInterval(timeUpdateInterval);
    };
  }, []);

  // Recalculate credits when component mounts or when credits might have changed
  useEffect(() => {
    const recalculated = initializeTokenBucket();
    setRemainingCredits(recalculated);
  }, []);

  // Save credits to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("api_credits", remainingCredits.toString());
  }, [remainingCredits]);

  useEffect(() => {
    loadCollections();
  }, []);

  // In-console: sync the selected endpoint from the ?e=<id> deep link once collections
  // are loaded (and whenever the link changes). Ignored on the standalone route.
  useEffect(() => {
    if (!insideConsole) return;
    const id = searchParams.get("e");
    if (!id) {
      setSelectedEndpoint(null);
      return;
    }
    if (collections.length) {
      const ep = findEndpointById(collections, id);
      if (ep && ep.id !== selectedEndpoint?.id) handleEndpointSelect(ep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insideConsole, searchParams, collections]);

  useEffect(() => {
    if (selectedEndpoint) {
      initializeRequestData();
      generateCommand();
    }
  }, [selectedEndpoint, environment]);

  useEffect(() => {
    // Auto-expand folders when searching
    if (searchQuery.trim()) {
      const expandMatchingFolders = (collections) => {
        collections.forEach((collection) => {
          const matchesName = collection.name
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
          const hasMatchingEndpoints = collection.endpoints?.some(
            (ep) =>
              ep.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              ep.path.toLowerCase().includes(searchQuery.toLowerCase())
          );

          if (
            matchesName ||
            hasMatchingEndpoints ||
            (collection.folders && collection.folders.length > 0)
          ) {
            setExpandedFolders((prev) => ({
              ...prev,
              [collection.pathKey]: true,
            }));
            if (collection.folders) {
              expandMatchingFolders(collection.folders);
            }
          }
        });
      };
      expandMatchingFolders(collections);
    }
  }, [searchQuery, collections]);

  const loadCollections = async () => {
    try {
      // Check if API key exists before making request
      const apiKey = localStorage.getItem("apiKey");
      if (!apiKey) {
        console.warn(
          "⚠️ No API key found - not making request to /api/apiCollection/docs"
        );
        message.error("Please login to access API documentation", 4);
        setLoadingCollections(false);
        return;
      }

      console.log(
        "✅ API key found, making request to /api/apiCollection/docs"
      );
      setLoadingCollections(true);
      // Fetch from API endpoint
      const response = await axiosInstance.get("/api/apiCollection/docs", {
        timeout: 10000, // 10 second timeout
      });

      if (response.status === 200 && response.data) {
        const collectionData = response.data;
        // Derive the brand's API domain from the doc's own URLs (first
        // "https://preprod-api.<domain>" or "https://api.<domain>" found), so the
        // displayed curl uses the same hosts as the served, brand-specific doc.
        const domainMatch = JSON.stringify(collectionData).match(
          /https:\/\/(?:preprod-api|api)\.([a-z0-9-]+\.[a-z]+)/i
        );
        if (domainMatch) setApiDomain(domainMatch[1].toLowerCase());
        const parsed = parsePostmanCollection(collectionData);
        if (parsed.length > 0) {
          setCollections(parsed);
        } else {
          message.error("No endpoints found in collection", 4);
        }
      } else {
        throw new Error("Failed to fetch collection");
      }
    } catch (error) {
      console.error("Error loading collection:", error);
      let errorMessage =
        "Failed to load API documentation. Please try again later.";

      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        errorMessage =
          "Request timed out. Please check if the backend server is running on localhost:5000";
      } else if (error.code === "ECONNREFUSED") {
        errorMessage =
          "Cannot connect to backend server. Please ensure it's running on localhost:5000";
      } else if (error.response) {
        // Check for API key related errors
        if (error.response.status === 400 || error.response.status === 401) {
          const responseError =
            error.response?.data?.error || error.response?.data?.message;
          if (
            responseError &&
            responseError.toLowerCase().includes("api key")
          ) {
            // Check if we actually have an API key stored
            const storedApiKey = localStorage.getItem("apiKey");
            if (storedApiKey) {
              // API key exists but is invalid/expired - let the interceptor handle it
              errorMessage =
                "API key is invalid or expired. Please login again.";
            } else {
              // No API key stored - user needs to login
              errorMessage =
                "API key is missing. Please login to access API documentation.";
            }
            // Don't clear localStorage here - let the interceptor handle it
            // This prevents race conditions on page refresh
          } else {
            errorMessage =
              responseError || `Server error: ${error.response.status}`;
          }
        } else {
          errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage, 5);
    } finally {
      setLoadingCollections(false);
    }
  };


  const renderMarkdown = (markdown) => {
    if (!markdown) return null;

    // First, extract and replace all tables
    const tables = [];
    let processedMarkdown = markdown;

    // Process line by line to detect tables more reliably
    const lines = markdown.split("\n");
    const tableBlocks = [];
    let currentTable = null;
    let tableStartCharIndex = 0;
    let charIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      const lineLength = line.length + 1; // +1 for newline

      // Check if line is a table row (starts and ends with |)
      if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
        if (!currentTable) {
          // Start of a new table
          currentTable = {
            lines: [line],
            startCharIndex: charIndex,
            startLineIndex: i,
          };
        } else {
          // Continue current table
          currentTable.lines.push(line);
        }
      } else {
        // Not a table row - end current table if exists
        if (currentTable && currentTable.lines.length >= 2) {
          // Verify it's a valid table (has header and separator)
          const tableText = currentTable.lines.join("\n");
          const tableRows = tableText
            .split("\n")
            .filter((r) => r.trim() && r.includes("|"));

          if (tableRows.length >= 2) {
            // Check if second row is a separator
            const secondRow = tableRows[1].trim();
            const isSeparator =
              /^\|[\s\-:]+\|$/.test(secondRow) ||
              secondRow
                .split("|")
                .slice(1, -1)
                .every((cell) => {
                  const trimmed = cell.trim();
                  return !trimmed || /^[\s\-:]+$/.test(trimmed);
                });

            if (isSeparator) {
              tableBlocks.push({
                text: tableText,
                startIndex: currentTable.startCharIndex,
                endIndex: currentTable.startCharIndex + tableText.length,
              });
            }
          }
        }
        currentTable = null;
      }

      charIndex += lineLength;
    }

    // Handle table at end of document
    if (currentTable && currentTable.lines.length >= 2) {
      const tableText = currentTable.lines.join("\n");
      const tableRows = tableText
        .split("\n")
        .filter((r) => r.trim() && r.includes("|"));

      if (tableRows.length >= 2) {
        const secondRow = tableRows[1].trim();
        const isSeparator =
          /^\|[\s\-:]+\|$/.test(secondRow) ||
          secondRow
            .split("|")
            .slice(1, -1)
            .every((cell) => {
              const trimmed = cell.trim();
              return !trimmed || /^[\s\-:]+$/.test(trimmed);
            });

        if (isSeparator) {
          tableBlocks.push({
            text: tableText,
            startIndex: currentTable.startCharIndex,
            endIndex: currentTable.startCharIndex + tableText.length,
          });
        }
      }
    }

    // Process each table block in reverse order
    for (let i = tableBlocks.length - 1; i >= 0; i--) {
      const block = tableBlocks[i];
      const tableText = block.text;
      const rows = tableText
        .split("\n")
        .filter((r) => r.trim() && r.includes("|"));

      if (rows.length >= 2) {
        // First row is header
        const headerRow = rows[0];
        const headerCells = headerRow.split("|").map((h) => h.trim());
        // Remove empty first and last cells
        const headers = headerCells.filter(
          (h, idx) => idx > 0 && idx < headerCells.length - 1
        );

        // Skip separator row (second row) and get data rows
        const dataRows = rows
          .slice(2)
          .filter((row) => {
            const cells = row.split("|").map((c) => c.trim());
            // Check if it's a separator row
            const isSeparator = cells.every(
              (cell) => !cell || /^[\s\-:]+$/.test(cell)
            );
            return !isSeparator;
          })
          .map((row) => {
            const cells = row.split("|").map((c) => c.trim());
            // Remove first and last empty cells
            return cells.filter((c, idx) => idx > 0 && idx < cells.length - 1);
          });

        if (headers.length > 0) {
          const placeholder = `__TABLE_PLACEHOLDER_${i}__`;
          tables.push({ headers, rows: dataRows, placeholder, index: i });
          // Replace from the end to preserve indices
          processedMarkdown =
            processedMarkdown.substring(0, block.startIndex) +
            placeholder +
            processedMarkdown.substring(block.endIndex);
        }
      }
    }

    // Now process the markdown without tables
    let html = processedMarkdown;

    // Replace table placeholders FIRST, before any other processing
    // This ensures placeholders aren't converted to paragraphs or processed as text
    // Process in reverse order to maintain correct indices
    for (let i = tables.length - 1; i >= 0; i--) {
      const table = tables[i];
      const tableHtml = `
        <div style="overflow-x: auto; margin: 24px 0; border-radius: 8px; border: 1px solid #e1e8f0;">
          <table style="min-width: 100%; width: max-content; border-collapse: collapse;">
            <thead style="background: #f8faff;">
              <tr>
                ${table.headers
                  .map((h) => {
                    // Remove bold markers from headers
                    let cleanHeader = h.replace(/\*\*/g, "").replace(/__/g, "");
                    cleanHeader = cleanHeader || "&nbsp;";
                    return `<th style="padding: 12px 16px; text-align: left; background: #f8faff; color: #262626; font-weight: 600; border-bottom: 1px solid #e1e8f0; white-space: nowrap;">${cleanHeader}</th>`;
                  })
                  .join("")}
              </tr>
            </thead>
            <tbody style="background: #ffffff;">
              ${
                table.rows.length > 0
                  ? table.rows
                      .map((row, rowIdx) => {
                        // Ensure row has same number of cells as headers
                        const paddedRow = [...row];
                        while (paddedRow.length < table.headers.length) {
                          paddedRow.push("");
                        }
                        return `
                  <tr style="border-bottom: 1px solid #f0f4ff; ${
                    rowIdx % 2 === 0
                      ? "background: #ffffff;"
                      : "background: #f8faff;"
                  }">
                    ${paddedRow
                      .slice(0, table.headers.length)
                      .map((cell) => {
                        // Process cell content (remove bold markers, handle links, etc.)
                        let cellContent = cell || "";
                        cellContent = cellContent.replace(
                          /\*\*(.*?)\*\*/g,
                          '<strong style="font-weight: 600;">$1</strong>'
                        );
                        cellContent = cellContent.replace(
                          /__([^_]+)__/g,
                          '<strong style="font-weight: 600;">$1</strong>'
                        );
                        cellContent = cellContent.replace(
                          /`([^`]+)`/g,
                          '<code style="background: #f0f4ff; padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 0.9em; color: #262626;">$1</code>'
                        );
                        cellContent = cellContent.replace(
                          /\[([^\]]+)\]\(([^)]+)\)/g,
                          '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #0066ff; text-decoration: none;">$1</a>'
                        );

                        // Description columns run long (the Output Parameters tables can be
                        // 60+ rows of prose). Cap the cell so it wraps into a readable
                        // column instead of stretching the table off-screen, and break
                        // long unbroken tokens (dotted field paths, URLs) rather than
                        // letting one of them set the table width.
                        return `<td style="padding: 12px 16px; border-right: 1px solid #f0f4ff; color: #262626; white-space: normal; overflow-wrap: anywhere; max-width: 520px;">${
                          cellContent || "&nbsp;"
                        }</td>`;
                      })
                      .join("")}
                  </tr>
                `;
                      })
                      .join("")
                  : '<tr><td colspan="' +
                    table.headers.length +
                    '" style="padding: 12px 16px; text-align: center; color: #6b7a99;">No data</td></tr>'
              }
            </tbody>
          </table>
        </div>
      `;
      // Keep the PLACEHOLDER for now and stash the built HTML — the table is substituted
      // only after block/inline processing below. Injecting it here used to force the
      // paragraph builder to split on HTML tags, which shattered every inline <code>/<b>
      // into its own block-level paragraph.
      table.html = tableHtml;
    }

    // Inline (span-level) markup only — applied INSIDE a block, never across blocks, so
    // `code` / **bold** stay part of their sentence instead of becoming their own blocks.
    const inline = (text) =>
      text
        .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
        .replace(/__(.*?)__/g, '<strong style="font-weight: 600;">$1</strong>')
        .replace(
          /`([^`]+)`/g,
          '<code style="background: #f0f4ff; padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 0.9em; color: #262626;">$1</code>'
        )
        .replace(
          /\[([^\]]+)\]\(([^)]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #0066ff; text-decoration: none;">$1</a>'
        );

    // Block-level pass: split on blank lines, then render each block by its own kind.
    // (Headings/rules/lists/tables are blocks; everything else is a paragraph.)
    const blocks = html.split(/\n{2,}/);
    let result = "";
    for (const rawBlock of blocks) {
      const block = rawBlock.trim();
      if (!block) continue;

      // A table placeholder occupies its own block — emit it untouched for substitution.
      if (/^__TABLE_PLACEHOLDER_\d+__$/.test(block)) {
        result += block;
        continue;
      }

      // Headings
      const heading = block.match(/^(#{1,3})\s+(.*)$/);
      if (heading) {
        const level = heading[1].length;
        const text = inline(heading[2].trim());
        if (level === 1) {
          result += `<h1 style="font-size: 30px; font-weight: 700; margin-top: 32px; margin-bottom: 16px; color: #0a1629;">${text}</h1>`;
        } else if (level === 2) {
          result += `<h2 style="font-size: 24px; font-weight: 700; margin-top: 32px; margin-bottom: 16px; color: #0a1629;">${text}</h2>`;
        } else {
          result += `<h3 style="font-size: 18px; font-weight: 600; margin-top: 24px; margin-bottom: 12px; color: #0066ff;">${text}</h3>`;
        }
        continue;
      }

      // Horizontal rule
      if (/^-{3,}$/.test(block)) {
        result += '<hr style="margin: 24px 0; border-color: #e1e8f0;" />';
        continue;
      }

      // Bullet list (a block whose every line starts with - or *)
      const listLines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      if (listLines.length && listLines.every((l) => /^[-*]\s+/.test(l))) {
        result +=
          '<ul style="margin: 0 0 16px 24px; padding: 0; color: #262626;">' +
          listLines
            .map(
              (l) =>
                `<li style="margin-bottom: 8px;">${inline(l.replace(/^[-*]\s+/, ""))}</li>`
            )
            .join("") +
          "</ul>";
        continue;
      }

      // Paragraph — single newlines inside it are soft breaks.
      result += `<p style="margin-bottom: 16px; color: #262626;">${inline(block).replace(
        /\n/g,
        "<br />"
      )}</p>`;
    }
    html = result;

    // Finally swap the table placeholders for their built HTML (kept out of the block
    // pass so the table markup is never re-parsed or wrapped in a paragraph).
    for (const table of tables) {
      while (html.includes(table.placeholder)) {
        html = html.replace(table.placeholder, table.html);
      }
    }

    return (
      <div
        style={{
          color: "#262626",
          lineHeight: 1.7,
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  const initializeRequestData = () => {
    if (!selectedEndpoint) return;

    const data = {};

    // Initialize body fields
    selectedEndpoint.body.forEach((field) => {
      if (field.type === "array") {
        data[field.key] = [];
      } else {
        data[field.key] =
          field.example !== undefined ? String(field.example) : "";
      }
    });

    // Initialize params
    selectedEndpoint.params.forEach((param) => {
      data[param.key] = "";
    });

    setRequestData(data);
  };

  const generateCommand = () => {
    if (!selectedEndpoint) return;

    const storedApiKey = localStorage.getItem("apiKey") || "";

    // Use display URL for cURL command (so users can copy and use it directly)
    const baseUrl = environmentDisplayUrls[environment];
    let url = `${baseUrl}${selectedEndpoint.path}`;

    // Replace path parameters
    selectedEndpoint.params.forEach((param) => {
      const value = requestData[param.key] || `{${param.key}}`;
      url = url.replace(`:${param.key}`, value);
    });

    let curlCommand = `curl -X ${selectedEndpoint.method} "${url}"`;

    // Always send JSON and API key header in curl command
    curlCommand += ` \\\n  -H "Content-Type: application/json"`;
    const apiKeyForCurl = storedApiKey || "<REPLACE_WITH_API_KEY>";
    curlCommand += ` \\\n  -H "x-api-key: ${apiKeyForCurl}"`;

    // Add body for POST/PUT
    if (["POST", "PUT", "PATCH"].includes(selectedEndpoint.method)) {
      const bodyData = {};
      selectedEndpoint.body.forEach((field) => {
        if (
          requestData[field.key] !== undefined &&
          requestData[field.key] !== ""
        ) {
          if (field.type === "array") {
            bodyData[field.key] = requestData[field.key];
          } else {
            bodyData[field.key] = requestData[field.key];
          }
        }
      });
      if (Object.keys(bodyData).length > 0) {
        curlCommand += ` \\\n  -d '${JSON.stringify(bodyData)}'`;
      }
    }

    setCommandText(curlCommand);
  };

  useEffect(() => {
    generateCommand();
    // apiDomain: the curl must re-render if the brand doc (and thus the host) loads late.
  }, [requestData, environment, selectedEndpoint, apiDomain]);

  const toggleFolder = (pathKey) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [pathKey]: !prev[pathKey],
    }));
  };

  const handleEndpointSelect = (endpoint) => {
    setSelectedEndpoint(endpoint);
    setResponse(null);
    setShowJsonView(false);
  };

  const handleInputChange = (key, value, isHeader = false) => {
    const dataKey = isHeader ? `header_${key}` : key;
    setRequestData((prev) => ({
      ...prev,
      [dataKey]: value,
    }));
  };

  const handleArrayAdd = (key) => {
    setRequestData((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), ""],
    }));
  };

  const handleArrayRemove = (key, index) => {
    setRequestData((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  const handleArrayItemChange = (key, index, value) => {
    setRequestData((prev) => ({
      ...prev,
      [key]: prev[key].map((item, i) => (i === index ? value : item)),
    }));
  };

  const executeRequest = async () => {
    if (!selectedEndpoint) return;

    // Check if user has credits remaining
    if (remainingCredits <= 0) {
      setLoading(false);
      setResponse({
        status: 429,
        statusText: "Too Many Requests",
        headers: {},
        data: {
          message: "Zero credit left",
          error:
            "You have exhausted all your API credits. Please contact support to replenish your credits.",
        },
        time: 0,
        error: true,
        config: {
          url: "",
          method: selectedEndpoint.method,
          headers: {},
          body: null,
        },
      });
      message.error("Zero credit left. Please contact support.", 4);
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const baseUrl = environments[environment];
      let url = `${baseUrl}${selectedEndpoint.path}`;

      // Replace path parameters
      let finalUrl = url;
      selectedEndpoint.params.forEach((param) => {
        const value = requestData[param.key];
        if (!value && param.required) {
          throw new Error(`Required parameter ${param.key} is missing`);
        }
        finalUrl = finalUrl.replace(`:${param.key}`, value || "");
      });

      // Headers are automatically added by axiosInstance (including x-api-key)
      // Build body
      let body = null;
      if (["POST", "PUT", "PATCH"].includes(selectedEndpoint.method)) {
        body = {};
        selectedEndpoint.body.forEach((field) => {
          const value = requestData[field.key];
          if (value !== undefined && value !== "") {
            body[field.key] = value;
          }
          if (field.required && (!value || value === "")) {
            throw new Error(`Required field ${field.key} is missing`);
          }
        });
      }

      // Make request using axiosInstance (automatically adds x-api-key header)
      const config = {
        method: selectedEndpoint.method.toLowerCase(),
        url: finalUrl,
        ...(body && { data: body }),
      };

      const startTime = Date.now();
      const apiResponse = await axiosInstance(config);
      const endTime = Date.now();

      setResponse({
        status: apiResponse.status,
        statusText: apiResponse.statusText,
        headers: apiResponse.headers,
        data: apiResponse.data,
        time: endTime - startTime,
        config: {
          url: finalUrl,
          method: selectedEndpoint.method,
          body,
        },
      });

      // Decrement credits after successful API call
      setRemainingCredits((prev) => {
        const newCredits = Math.max(0, prev - 1);
        return newCredits;
      });

      message.success("Request successful!", 3);
    } catch (error) {
      const endTime = Date.now();
      setResponse({
        status: error.response?.status || 0,
        statusText: error.response?.statusText || "Error",
        headers: error.response?.headers || {},
        data: error.response?.data || { error: error.message },
        time: Date.now() - (Date.now() - (endTime - Date.now())),
        error: true,
        config: {
          url: error.config?.url || "",
          method: selectedEndpoint.method,
          headers: error.config?.headers || {},
          body: error.config?.data || null,
        },
      });
      message.error(
        error.response?.data?.message || error.message || "Request failed",
        4
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard!", 2);
  };

  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  const flattenObject = (obj, prefix = "") => {
    const flattened = [];
    for (const key in obj) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        flattened.push(...flattenObject(value, newKey));
      } else {
        flattened.push({
          key: newKey,
          value: Array.isArray(value) ? JSON.stringify(value) : String(value),
        });
      }
    }
    return flattened;
  };

  const filterCollections = (collections, query) => {
    if (!query.trim()) return collections;

    const lowerQuery = query.toLowerCase();
    const filtered = [];

    const searchInCollection = (collection) => {
      const matchesName = collection.name.toLowerCase().includes(lowerQuery);
      const matchingEndpoints = collection.endpoints
        ? collection.endpoints.filter(
            (ep) =>
              ep.name.toLowerCase().includes(lowerQuery) ||
              ep.path.toLowerCase().includes(lowerQuery)
          )
        : [];
      const matchingFolders = collection.folders
        ? collection.folders
            .map((folder) => searchInCollection(folder))
            .filter((f) => f !== null)
        : [];

      if (
        matchesName ||
        matchingEndpoints.length > 0 ||
        matchingFolders.length > 0
      ) {
        return {
          ...collection,
          endpoints: matchesName
            ? collection.endpoints || []
            : matchingEndpoints,
          folders: matchingFolders,
        };
      }
      return null;
    };

    collections.forEach((collection) => {
      const result = searchInCollection(collection);
      if (result) filtered.push(result);
    });

    return filtered;
  };

  const renderCollectionTree = (collection) => {
    const isExpanded =
      expandedFolders[collection.pathKey] || searchQuery.trim();
    const hasContent =
      (collection.folders && collection.folders.length > 0) ||
      (collection.endpoints && collection.endpoints.length > 0);

    const collapseItems = [
      {
        key: collection.pathKey,
        label: (
          <Space>
            <BookOutlined style={{ color: "#0066ff" }} />
            <Typography.Text strong>{collection.name}</Typography.Text>
          </Space>
        ),
        children: (
          <Space orientation="vertical" style={{ width: "100%" }}>
            {collection.folders &&
              collection.folders.map((folder) => renderCollectionTree(folder))}
            {collection.endpoints &&
              collection.endpoints.map((endpoint) => (
                <Button
                  key={endpoint.id}
                  type={
                    selectedEndpoint?.id === endpoint.id ? "primary" : "default"
                  }
                  onClick={() => handleEndpointSelect(endpoint)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    paddingLeft: 24,
                  }}
                >
                  <Tag
                    color={
                      endpoint.method === "GET"
                        ? "green"
                        : endpoint.method === "POST"
                        ? "blue"
                        : endpoint.method === "PUT"
                        ? "orange"
                        : endpoint.method === "DELETE"
                        ? "red"
                        : "default"
                    }
                    style={{ minWidth: 50, textAlign: "center" }}
                  >
                    {endpoint.method}
                  </Tag>
                  <Typography.Text ellipsis={true}>
                    {endpoint.name}
                  </Typography.Text>
                </Button>
              ))}
          </Space>
        ),
        style: {
          background: "#ffffff",
          borderRadius: 6,
          border: "1px solid #e1e8f0",
          overflow: "hidden",
        },
      },
    ];

    return (
      <Collapse
        key={collection.pathKey}
        bordered={false}
        activeKey={isExpanded ? [collection.pathKey] : []}
        onChange={() => toggleFolder(collection.pathKey)}
        expandIcon={({ isActive }) =>
          isActive ? <DownOutlined /> : <RightOutlined />
        }
        items={collapseItems}
        style={{ background: "transparent", marginBottom: 8 }}
      />
    );
  };

  return (
    <DashboardLayout title="API Documentation">
      <Layout
        style={{ background: "#f0f4ff", minHeight: "calc(100vh - 72px)" }}
      >
        {!insideConsole && (
        <Sider
          width={300}
          style={{
            background: "#ffffff",
            padding: "16px",
            borderRight: "1px solid #e1e8f0",
            overflowY: "auto",
          }}
        >
          <Space
            orientation="vertical"
            size="middle"
            style={{ width: "100%", marginBottom: "16px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Title level={4} style={{ margin: 0, color: "#262626" }}>
                Collections
              </Title>
              <Tag
                icon={<DollarOutlined />}
                color={
                  remainingCredits > 10
                    ? "green"
                    : remainingCredits > 0
                    ? "orange"
                    : "red"
                }
                style={{ padding: "4px 8px", fontSize: "12px" }}
                title={`Token Bucket: ${remainingCredits}/${BUCKET_CAPACITY} credits. Refills ${REFILL_RATE} token per minute. Next refill in ${nextRefillInSeconds}s.`}
              >
                Credits: {remainingCredits}/{BUCKET_CAPACITY}
              </Tag>
            </div>
            <Input
              placeholder="Search APIs..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </Space>
          <div style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
            <Spin
              spinning={loadingCollections}
              tip="Loading API documentation..."
            >
              {!loadingCollections && (
                <>
                  {filterCollections(collections, searchQuery).map(
                    (collection) => renderCollectionTree(collection)
                  )}
                  {filterCollections(collections, searchQuery).length === 0 &&
                    searchQuery && (
                      <Empty
                        description={`No APIs found matching "${searchQuery}"`}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  {filterCollections(collections, searchQuery).length === 0 &&
                    !searchQuery && (
                      <Empty
                        description="No API collections available"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                </>
              )}
            </Spin>
          </div>
        </Sider>
        )}

        <AntContent style={{ padding: "16px" }}>
          {selectedEndpoint ? (
            <Card
              style={{
                borderRadius: 12,
                boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
                minHeight: "100%",
              }}
            >
              <Space
                orientation="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Space orientation="vertical" size="small">
                    <Space>
                      <Tag
                        color={
                          selectedEndpoint.method === "GET"
                            ? "green"
                            : selectedEndpoint.method === "POST"
                            ? "blue"
                            : selectedEndpoint.method === "PUT"
                            ? "orange"
                            : selectedEndpoint.method === "DELETE"
                            ? "red"
                            : "default"
                        }
                        style={{ minWidth: 60, textAlign: "center" }}
                      >
                        {selectedEndpoint.method}
                      </Tag>
                      <Title level={3} style={{ margin: 0, color: "#262626" }}>
                        {selectedEndpoint.name}
                      </Title>
                    </Space>
                    <Typography.Text code style={{ fontSize: "14px" }}>
                      {selectedEndpoint.path}
                    </Typography.Text>
                  </Space>
                  <Space>
                    <Tag
                      icon={<DollarOutlined />}
                      color={
                        remainingCredits > 10
                          ? "green"
                          : remainingCredits > 0
                          ? "orange"
                          : "red"
                      }
                      style={{ padding: "4px 8px" }}
                    >
                      Credits: {remainingCredits}/{BUCKET_CAPACITY}
                    </Tag>
                    <Select
                      value={environment}
                      onChange={(value) => setEnvironment(value)}
                      style={{ width: 120 }}
                      suffixIcon={<GlobalOutlined />}
                    >
                      <Option value="preprod">Preprod</Option>
                      <Option value="prod">Production</Option>
                    </Select>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={() => {
                        setActiveTab("request");
                        setViewMode("input");
                      }}
                      style={{
                        background:
                          "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                        border: "none",
                        fontWeight: 600,
                      }}
                    >
                      Try
                    </Button>
                  </Space>
                </div>

                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={[
                    {
                      label: (
                        <Space>
                          <FileTextOutlined /> Documentation
                        </Space>
                      ),
                      key: "documentation",
                      children: (
                        <div style={{ padding: "16px 0" }}>
                          {renderMarkdown(selectedEndpoint.description)}
                        </div>
                      ),
                    },
                    {
                      label: (
                        <Space>
                          <CodeOutlined /> Request
                        </Space>
                      ),
                      key: "request",
                      children: (
                        <Space
                          orientation="vertical"
                          size="large"
                          style={{ width: "100%" }}
                        >
                          <Tabs
                            defaultActiveKey="input"
                            activeKey={viewMode}
                            onChange={setViewMode}
                            items={[
                              {
                                label: "Input View",
                                key: "input",
                                children: (
                                  <Space
                                    orientation="vertical"
                                    size="middle"
                                    style={{ width: "100%" }}
                                  >
                                    {selectedEndpoint.params.length > 0 && (
                                      <Card
                                        title="Path Parameters"
                                        size="small"
                                        style={{ borderRadius: 6 }}
                                      >
                                        <Space
                                          orientation="vertical"
                                          style={{ width: "100%" }}
                                        >
                                          {selectedEndpoint.params.map(
                                            (param) => (
                                              <Form.Item
                                                key={param.key}
                                                label={
                                                  <span>
                                                    {param.key}
                                                    {param.required && (
                                                      <span
                                                        style={{
                                                          color: "#ff3366",
                                                          marginLeft: 4,
                                                        }}
                                                      >
                                                        *
                                                      </span>
                                                    )}
                                                    {param.description && (
                                                      <span
                                                        style={{
                                                          color: "#6b7a99",
                                                          marginLeft: 4,
                                                        }}
                                                      >
                                                        ({param.description})
                                                      </span>
                                                    )}
                                                  </span>
                                                }
                                                style={{ marginBottom: 0 }}
                                              >
                                                <Input
                                                  value={
                                                    requestData[param.key] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleInputChange(
                                                      param.key,
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder={param.key}
                                                />
                                              </Form.Item>
                                            )
                                          )}
                                        </Space>
                                      </Card>
                                    )}

                                    {selectedEndpoint.body.length > 0 && (
                                      <Card
                                        title="Request Body"
                                        size="small"
                                        style={{ borderRadius: 6 }}
                                      >
                                        <Space
                                          orientation="vertical"
                                          style={{ width: "100%" }}
                                        >
                                          {selectedEndpoint.body.map(
                                            (field) => (
                                              <Form.Item
                                                key={field.key}
                                                label={
                                                  <span>
                                                    {field.key}
                                                    {field.required && (
                                                      <span
                                                        style={{
                                                          color: "#ff3366",
                                                          marginLeft: 4,
                                                        }}
                                                      >
                                                        *
                                                      </span>
                                                    )}
                                                    {field.type && (
                                                      <span
                                                        style={{
                                                          color: "#6b7a99",
                                                          marginLeft: 4,
                                                        }}
                                                      >
                                                        ({field.type})
                                                      </span>
                                                    )}
                                                  </span>
                                                }
                                                style={{ marginBottom: 0 }}
                                              >
                                                {field.type === "array" ? (
                                                  <Space
                                                    orientation="vertical"
                                                    style={{ width: "100%" }}
                                                  >
                                                    {(
                                                      requestData[field.key] ||
                                                      []
                                                    ).map((item, index) => (
                                                      <Input.Group
                                                        compact
                                                        key={index}
                                                      >
                                                        <Input
                                                          style={{
                                                            width:
                                                              "calc(100% - 60px)",
                                                          }}
                                                          value={item}
                                                          onChange={(e) =>
                                                            handleArrayItemChange(
                                                              field.key,
                                                              index,
                                                              e.target.value
                                                            )
                                                          }
                                                          placeholder={`${field.key}[${index}]`}
                                                        />
                                                        <Button
                                                          danger
                                                          icon={
                                                            <CloseCircleOutlined />
                                                          }
                                                          onClick={() =>
                                                            handleArrayRemove(
                                                              field.key,
                                                              index
                                                            )
                                                          }
                                                        />
                                                      </Input.Group>
                                                    ))}
                                                    <Button
                                                      type="dashed"
                                                      onClick={() =>
                                                        handleArrayAdd(
                                                          field.key
                                                        )
                                                      }
                                                      block
                                                    >
                                                      Add Item
                                                    </Button>
                                                  </Space>
                                                ) : (
                                                  <Input
                                                    value={
                                                      requestData[field.key] ||
                                                      ""
                                                    }
                                                    onChange={(e) =>
                                                      handleInputChange(
                                                        field.key,
                                                        e.target.value
                                                      )
                                                    }
                                                    placeholder={
                                                      field.example !==
                                                      undefined
                                                        ? String(field.example)
                                                        : field.description ||
                                                          field.key
                                                    }
                                                  />
                                                )}
                                              </Form.Item>
                                            )
                                          )}
                                        </Space>
                                      </Card>
                                    )}
                                  </Space>
                                ),
                              },
                              {
                                label: "cURL Command",
                                key: "command",
                                children: (
                                  <Space
                                    orientation="vertical"
                                    style={{ width: "100%" }}
                                  >
                                    <Button
                                      icon={<CopyOutlined />}
                                      onClick={() =>
                                        copyToClipboard(commandText)
                                      }
                                    >
                                      Copy Command
                                    </Button>
                                    <TextArea
                                      value={commandText}
                                      autoSize={{ minRows: 5, maxRows: 15 }}
                                      readOnly
                                      style={{ fontFamily: "monospace" }}
                                    />
                                  </Space>
                                ),
                              },
                            ]}
                          />
                          <div>
                            <Button
                              type="primary"
                              icon={
                                loading ? (
                                  <LoadingOutlined />
                                ) : (
                                  <PlayCircleOutlined />
                                )
                              }
                              onClick={executeRequest}
                              loading={loading}
                              disabled={remainingCredits <= 0}
                              style={{
                                background:
                                  "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                                border: "none",
                                fontWeight: 600,
                              }}
                            >
                              {remainingCredits <= 0 ? "No Credits" : "Send"}
                            </Button>
                          </div>
                          {(response || loading) && (
                            <>
                              <Divider orientation="left" style={{ margin: 0 }}>
                                Response
                              </Divider>
                              <Spin spinning={loading}>
                          {response ? (
                            <Space
                              orientation="vertical"
                              size="middle"
                              style={{ width: "100%" }}
                            >
                              <Alert
                                title={
                                  <Space>
                                    {response.error ? (
                                      <CloseCircleOutlined
                                        style={{ color: "red" }}
                                      />
                                    ) : (
                                      <CheckCircleOutlined
                                        style={{ color: "green" }}
                                      />
                                    )}
                                    <Typography.Text strong>
                                      Status: {response.status}{" "}
                                      {response.statusText} ({response.time}ms)
                                    </Typography.Text>
                                  </Space>
                                }
                                type={response.error ? "error" : "success"}
                                showIcon
                                style={{ borderRadius: 6 }}
                              />
                              <Tabs
                                defaultActiveKey="json"
                                items={[
                                  {
                                    label: (
                                      <Space>
                                        <CodeOutlined /> JSON
                                      </Space>
                                    ),
                                    key: "json",
                                    children: (
                                      <Space
                                        orientation="vertical"
                                        style={{ width: "100%" }}
                                      >
                                        <Button
                                          icon={<CopyOutlined />}
                                          onClick={() =>
                                            copyToClipboard(
                                              formatJson(response.data)
                                            )
                                          }
                                          style={{ marginBottom: 8 }}
                                        >
                                          Copy Response
                                        </Button>
                                        <TextArea
                                          value={formatJson(response.data)}
                                          autoSize={{
                                            minRows: 10,
                                            maxRows: 30,
                                          }}
                                          readOnly
                                          style={{ fontFamily: "monospace" }}
                                        />
                                      </Space>
                                    ),
                                  },
                                  {
                                    label: (
                                      <Space>
                                        <TableOutlined /> Table
                                      </Space>
                                    ),
                                    key: "table",
                                    children: (
                                      <Table
                                        dataSource={flattenObject(
                                          response.data
                                        )}
                                        columns={[
                                          {
                                            title: "Key",
                                            dataIndex: "key",
                                            key: "key",
                                          },
                                          {
                                            title: "Value",
                                            dataIndex: "value",
                                            key: "value",
                                          },
                                        ]}
                                        pagination={false}
                                        rowKey="key"
                                        size="small"
                                        scroll={{ y: 600 }}
                                      />
                                    ),
                                  },
                                ]}
                              />
                            </Space>
                          ) : (
                            <div style={{ minHeight: 48 }} />
                          )}
                              </Spin>
                            </>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                />
              </Space>
            </Card>
          ) : (
            <Empty description="Select an API from the left panel to view documentation and test." />
          )}
        </AntContent>
      </Layout>
    </DashboardLayout>
  );
}
